import React, { useEffect, useState } from 'react';
import ReactTable from 'react-table';
import moment from 'moment-timezone';

import { ChecklistImage } from '../Checklist';
import { PastChecklistsService, TimeFormattingService } from '../../services';
import i18next from "i18next";
import { connect } from 'react-redux';

const CorrectiveActionsReportStep = connect(({ user }) => ({ me: user.me }))(({ step, me }) => {
  const addFontWeight = {'fontWeight': '500'};
  const stepName = (<span key={step.id}>{step.checklistTaskOptionConfig.name}:</span>);
  switch (step.type) {
    case 'selection':
      const selections = step.pastChecklistTaskSelectedOptions.map(selection => selection.checklistTaskSelectionOption.name);
      return <span>{stepName} <span style={addFontWeight}>{selections.join(', ')}</span></span>
    case 'temperature':
      const temp = step.temperature ? 
          (me.temperatureDisplayUnit == "F" ? sprintf("%0.2f F", (step.temperature * 1.8) + 32) : sprintf("%0.2f C", step.temperature)) : 
          '';
      return <span>{stepName} <span style={addFontWeight}>{temp}</span></span>;
    case 'photo':
      return step.pastChecklistTaskPhotos ? <span>{stepName} {step.pastChecklistTaskPhotos.map((photo, index) => <div key={index}><ChecklistImage imgUrl={photo.photoURL}/></div>)}</span> : <span></span>;
    case 'note':
      return (<span>{stepName} <span style={addFontWeight}>{step.note}</span></span>);
    default: return null;
  }
});

const CorrectiveActionsReportTime = ({ data, timezone }) => (
  <span>{TimeFormattingService.renderDate(data, timezone )}<br/>{TimeFormattingService.renderTime(data, timezone)}</span>
);

const CorrectiveActionsReportDetailTable = ({ rows, timezone }) => rows.length == 0 ? (<div>No results</div>) : (
  <ReactTable
    className="-striped -highlight"
    data={rows}
    showPagination={false}
    pageSize={rows.length}
    defaultSorted={[{
      id: 'taskCompleted',
      desc: false // not necessary unless set to true, but it's good to know its there.
    }]}
    columns={[{
        Header: i18next.t('table-headers.checklist'),
        accessor: "checklistName",
        Cell: ({value, original}) => <span>{value} (V{original.checklistVersion})</span>,
        minWidth: 100,
        minResizeWidth: 100
      }, {
        Header: i18next.t('table-headers.task'),
        accessor: "taskName",
        style:{ 'whiteSpace': 'unset'},
        minWidth: 100,
        minResizeWidth: 100
      }, {
        Header: i18next.t('table-headers.step'),
        accessor: "checklistTaskStepTaken",
        Cell: ({value}) => <CorrectiveActionsReportStep step={value} />,
        style:{ 'whiteSpace': 'normal'},
        minWidth: 100,
        minResizeWidth: 100
      }, {
        Header: "Task Completed At",
        accessor: "taskCompleted", 
        Cell: ({value}) => value ? <CorrectiveActionsReportTime data={value} timezone={timezone ?? null} /> : '',
        minWidth: 100,
        minResizeWidth: 100
      }, {
        Header: i18next.t('table-headers.corrective-action'),
        accessor: "correctiveActionTaken.pastChecklistTaskOptions",
        Cell: ({value}) => value ? value.map(step => (<CorrectiveActionsReportStep step={step} />)) : '',
        style:{ 'whiteSpace': 'normal'},
        minWidth: 100,
        minResizeWidth: 100
      }, {
        Header: "Action Completed At",
        accessor: "correctiveActionTaken.completedAt",
        Cell: ({value}) => value ? <CorrectiveActionsReportTime data={value} timezone={timezone ?? null} /> : '',
        minWidth: 100,
        minResizeWidth: 100
      }
    ]}
  />
);

const CorrectiveActionsReportSummaryArea = ({ summary }) => (
  <div className='row'>
    <div className='col-sm-3'>&nbsp;</div>
    <div className='col-sm-2' style={{textAlign: 'center', borderRight: '1px solid lightgray', padding: '20 20 10 10'}}>
      <p style={{fontWeight: 'bold', fontSize: 28}}>{summary.correctiveActionsNeededCount}</p>
      Corrective Actions Needed
    </div>
    <div className='col-sm-2' style={{textAlign: 'center', borderRight: '1px solid lightgray', padding: '20 20 10 10'}}>
      <p style={{fontWeight: 'bold', fontSize: 28}}>
        {Math.round(summary.correctiveActionsCompletedCount/summary.correctiveActionsNeededCount*100)}%
        &nbsp;
        <span style={{fontWeight: 'normal', fontSize: 16}}>({summary.correctiveActionsCompletedCount}/{summary.correctiveActionsCompletedCount})</span>
      </p>
      Corrective Actions Completed
    </div>
    <div className='col-sm-2' style={{textAlign: 'center', padding: '20 20 10 10'}}>
      <p style={{fontWeight: 'bold', fontSize: 28}}>{summary.averageCorrectiveActionCompletionTime == 0 ? '0' : moment.duration(summary.averageCorrectiveActionCompletionTime).humanize()}</p>
      Average Time to Complete CA
    </div>
    <div className='col-sm-3'>&nbsp;</div>
  </div>
);

const CorrectiveActionsReportTaskResult = ({ singleLocation, singleChecklist, summary, rows }) => {
  let columns = [{
    Header: 'Task',
    accessor: "taskName",
    minWidth: 100,
    minResizeWidth: 100
  }];
  
  if (singleLocation == false) {
    columns = [...columns, {
      Header: 'Location',
      accessor: "locationName",
      minWidth: 100,
      minResizeWidth: 100
    }];
  }

  if (singleChecklist == false) {
    columns = [...columns, {
      Header: 'Checklist',
      accessor: "checklistName",
      Cell: ({value, original}) => `${value} (V${original.checklistVersion})`,
      minWidth: 100,
      minResizeWidth: 100
    }];
  }

  columns = [...columns, {
      Header: 'Completed Tasks',
      accessor: "completedCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Required Corrective Actions',
      accessor: "correctiveActionRequiredCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Completed Corrective Actions',
      accessor: "correctiveActionCompletedCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Average Corrective Action Completion Time',
      accessor: "averageCompletionTime",
      Cell: ({value}) => moment.duration(value).humanize(),
      minWidth: 100,
      minResizeWidth: 100
    },
  ];

  if (rows.length == 0) {
    return <div>No results</div>;
  } else {
    return (
      <div>
        <CorrectiveActionsReportSummaryArea summary={summary} />
        <ReactTable
          className="-striped -highlight"
          data={rows}
          showPagination={false}
          pageSize={rows.length}
          defaultSorted={[{
            id: 'taskName',
            desc: false // not necessary unless set to true, but it's good to know its there.
          }]}
          columns={columns}
        />
      </div>
    );
  }
};

const CorrectiveActionsReportLocationResult = ({ singleChecklist, summary, rows }) => {
  let columns = [{
      Header: 'Location',
      accessor: "locationName",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Completed Checklists',
      accessor: "completedChecklistCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Average Completion Time',
      accessor: "averageCorrectiveActionCompletionTime",
      Cell: ({value}) => moment.duration(value).humanize(),
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Required Corrective Actions',
      accessor: "requiredCorrectiveActionCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Completed Corrective Actions',
      accessor: "completedCorrectiveActionCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Corrective Action',
      accessor: "topCorrectiveActionTaskName",
      minWidth: 100,
      minResizeWidth: 100
    }
  ];

  if (singleChecklist == false) {
    columns = [...columns, {
      Header: 'Checklist With Most Corrective Actions',
      accessor: "topCAChecklistName",
      Cell: ({value, original}) => `${value} (V${original.topCAChecklistVersion})`,
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Count',
      accessor: "caCountForTopCAChecklist",
      minWidth: 100,
      minResizeWidth: 100
    }];
  }

  if (rows.length == 0) {
    return <div>No results</div>;
  } else {
    return (
      <div>
        <CorrectiveActionsReportSummaryArea summary={summary} />
        <ReactTable
          className="-striped -highlight"
          data={rows}
          showPagination={false}
          pageSize={rows.length}
          defaultSorted={[{
            id: 'locationName',
            desc: false // not necessary unless set to true, but it's good to know its there.
          }]}
          columns={columns}
        />
      </div>
    );
  }
}

const CorrectiveActionsReportChecklistResult = ({ singleLocation, summary, rows }) => {
  let columns = [{
    Header: i18next.t('table-headers.checklist'),
    accessor: "checklistName",
    Cell: ({value, original}) => `${value} (V${original.checklistVersion})`,
    minWidth: 100,
    minResizeWidth: 100,
    width: 300
  }];
  
  if (singleLocation == false) {
    columns.push({
      Header: 'Location',
      accessor: "locationName",
      minWidth: 100,
      minResizeWidth: 100
    });
  }

  columns.push({
      Header: 'Completed',
      accessor: "completedCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Average Completion Time',
      accessor: "averageCorrectiveActionCompletionTime",
      Cell: ({value}) => moment.duration(value).humanize(),
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Required Corrective Actions',
      accessor: "requiredCorrectiveActionCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Completed Corrective Actions',
      accessor: "completedCorrectiveActionCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Task With Most Corrective Actions',
      accessor: "topTaskWithMostCorrectiveActionsName",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Count',
      accessor: "topTaskWithMostCorrectiveActionsCount",
      minWidth: 100,
      minResizeWidth: 100
    }, {
      Header: 'Corrective Action',
      accessor: "topCorrectiveActionTaskName",
      minWidth: 100,
      minResizeWidth: 100
    }
  );
  
  if (rows.length == 0) {
    return <div>No results</div>;
  } else {
    return (
      <div>
        <CorrectiveActionsReportSummaryArea summary={summary} />
        <ReactTable
          className="-striped -highlight"
          data={rows}
          showPagination={false}
          pageSize={rows.length}
          defaultSorted={[{
            id: 'checklistName',
            desc: false // not necessary unless set to true, but it's good to know its there.
          }]}
          columns={columns}
        />
      </div>
    );
  }
}

const CorrectiveActionsReportResults = ({ query }) => {
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    setLoading(true);
    setSummaryData(null);
    setTableData(null);

    const { focus, dateRange, startDate, selectedGrouping, selectedChecklist } = query;

    var startAt = moment(startDate);
    var endAt = dateRange.label === i18next.t("day") ? moment(startDate).add(1, "day") : moment(startDate).add(6, "day");

    startAt = selectedGrouping.grouping.timezone ? startAt.tz(selectedGrouping.grouping.timezone).startOf("day").valueOf() : startAt.startOf("day").utc().valueOf();
    endAt = selectedGrouping.grouping.timezone ? endAt.tz(selectedGrouping.grouping.timezone).endOf("day").valueOf() : endAt.endOf("day").utc().valueOf();

    let options = [
      { focus: focus },
      { groupingId: selectedGrouping.grouping.id },
      { startDate: startAt },
      { endDate: endAt }
    ];

    if (focus !== 'detail' && selectedChecklist) {
      options.push({ checklistId: selectedChecklist.value });
    } else if (focus === 'detail' && selectedChecklist && selectedChecklist.value != -1) {
      options.push({ checklistId: selectedChecklist.value });
    }

    PastChecklistsService.getCorrectiveActionReport(options).then(({ report }) => {
      if (report.correctiveActionReportResults) {
        setTableData(report.correctiveActionReportResults);
      } else {
        setSummaryData(report.summary);
        setTableData(report.rows);
      }
      setLoading(false);
    }).catch((error) => {
      console.error(error);
      setLoading(false)
    });
  }, [query]);
  
  if (loading) {
    return (<div className="loader">{i18next.t('loading')}</div>);
  } else {
    return (
      <div>
        <div className="op-content-box">
          <ul className="list-inline clearfix">
            <li className="pull-left box-label"><h4 className="remove-margin-bottom">{i18next.t('results')}</h4></li>
          </ul>
          
          <div>
            <div className="react-table-custom">
              { query.focus === 'detail' && <CorrectiveActionsReportDetailTable rows={tableData} timezone={query.selectedGrouping.grouping.timezone} /> }
              { query.focus === 'task' && <CorrectiveActionsReportTaskResult singleLocation={query.selectedGrouping.value > -1} singleChecklist={query.selectedChecklist.value > -1} summary={summaryData} rows={tableData} timezone={query.selectedGrouping.grouping.timezone} /> }
              { query.focus === 'checklist' && <CorrectiveActionsReportChecklistResult singleLocation={query.selectedGrouping.value > -1} summary={summaryData} rows={tableData} timezone={query.selectedGrouping.grouping.timezone} /> }
              { query.focus === 'location' && <CorrectiveActionsReportLocationResult singleChecklist={query.selectedChecklist.value > -1} summary={summaryData} rows={tableData} timezone={query.selectedGrouping.grouping.timezone} /> }
            </div> 
          </div>
        </div>
      </div>
    );
  }
}

export default CorrectiveActionsReportResults;
